import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { get } from 'lodash-es';
/* Models */
import { VisitorModel } from 'client/data/models/visitor';
import { FinancingModel, PATHS } from 'client/data/models/financing';
/* Utils */
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import {
  getFinancingLeadSubmissionInfo,
  getConsumerEmailData,
  getPrequalEmailData,
  getDecryptedFinancialFields,
  getUserData,
  isE2ETest,
} from 'site-modules/financing/utils/financing-utils';
import { getDecryptedFields } from 'client/site-modules/financing/utils/encrypting-utils';
import { logger } from 'client/utils/logger/logger';
import { EventToolbox } from 'client/utils/event-toolbox';
import { sendMarketoEmailReport } from 'client/data/utils/marketo-service';
import {
  getFinancingPrequalifiedCookie,
  setFinancingPrequalifiedCookie,
} from 'site-modules/shared/utils/financing/financing-prequalified-cookie';
import { sendMockLeadResponse, shouldMockResponse } from 'site-modules/shared/utils/financing/mock-util';
/* Constants */
import { ClientConfig } from 'client/configuration';
import { EdmundsAPI } from 'client/data/api/api-client';
import { DIGITAL_RETAIL_URL } from 'client/site-modules/financing/constants/urls';
import { FINANCING_EVENTS } from 'site-modules/shared/constants/financing/financing-events';
import { MOCK_PERSONAL_INFO } from 'site-modules/shared/constants/financing/mock-financing-data';

export function calculateFinancing(WrappedComponent) {
  class CalculateFinancing extends Component {
    clearFinancingPath = () => {
      const { setModelValue } = this.props;
      setModelValue(PATHS.PREQUALIFICATION_DATA, FinancingModel, undefined);
      setFinancingPrequalifiedCookie(undefined);
    };

    submitLead = async (targetEl, { financingOffer, calculatorSelections }) => {
      const { sessionId, visitorId, vin, vehicle, prequalificationData, location } = this.props;
      const personalInfoDecrypted = isE2ETest(location)
        ? MOCK_PERSONAL_INFO
        : await getDecryptedFields(JSON.parse(prequalificationData));
      if (shouldMockResponse(personalInfoDecrypted)) {
        return sendMockLeadResponse();
      }

      const { submissionInfo, emailTokens } = getFinancingLeadSubmissionInfo({
        sessionId,
        visitorId,
        vin,
        vehicle,
        financingOffer,
        calculatorSelections,
        personalInfo: personalInfoDecrypted,
      });

      EventToolbox.fireCustomEvent(FINANCING_EVENTS.LEAD_FORM_ATTEMPT_SUBMIT, {}, targetEl);

      try {
        const response = await EdmundsAPI.fetchJson(DIGITAL_RETAIL_URL, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'x-visitor-id': visitorId,
          },
          timeout: ClientConfig.get('carBuying').apiDefaultTimeoutMs,
          showAPIError: true,
          body: JSON.stringify(submissionInfo),
        });

        if (response && response.status === 'OK') {
          EventToolbox.fireCustomEvent(FINANCING_EVENTS.LEAD_FORM_PROCESSED_SUBMIT, {}, targetEl);
          EventToolbox.fireCustomEvent(FINANCING_EVENTS.LEAD_FORM_SUBMIT, {}, targetEl);

          await sendMarketoEmailReport({
            data: emailTokens,
            campaignName: 'capital_one_lead_confirmation_email',
            email: get(personalInfoDecrypted, 'email', ''),
          });

          EventToolbox.fireCustomEvent(FINANCING_EVENTS.SEND_CONFIRMATION_EMAIL, {
            value: 'Dealer Sales Lead Confirmation Email',
          });
        }

        return response;
      } catch (e) {
        logger.error(`Post lead error - ${e}`);
        return null;
      }
    };

    sendConsumerEmail = async decisionStatus => {
      const prequalifyData = getFinancingPrequalifiedCookie();
      let decryptedPrequalifyData;

      if (prequalifyData) {
        decryptedPrequalifyData = await getDecryptedFields(JSON.parse(prequalifyData));
      } else {
        const financingFieldsData = await getDecryptedFinancialFields();
        decryptedPrequalifyData = getUserData(financingFieldsData);
      }

      const emailTokens = getConsumerEmailData({
        status: decisionStatus,
      });

      await sendMarketoEmailReport({
        data: emailTokens,
        campaignName: 'capital_one_consumer_email',
        email: get(decryptedPrequalifyData, 'email', ''),
      });

      EventToolbox.fireCustomEvent(FINANCING_EVENTS.SEND_CONSUMER_EMAIL, {
        emailType: decisionStatus,
      });
    };

    sendPreQualTermEmail = async ({ email, dealerInfo, offer }) => {
      const { vehicle, vin, prequalificationData } = this.props;
      const personalInfo = JSON.parse(prequalificationData);
      const personalInfoDecrypted = await getDecryptedFields(personalInfo);
      const emailTokens = getPrequalEmailData({ vehicle, vin, storageData: personalInfoDecrypted, dealerInfo, offer });

      await sendMarketoEmailReport({
        data: emailTokens,
        campaignName: 'capital_one_prequal_term_email',
        email: email || get(personalInfoDecrypted, 'email', ''),
      });
    };
    render() {
      return (
        <WrappedComponent
          {...this.props}
          {...this.state}
          submitLead={this.submitLead}
          sendConsumerEmail={this.sendConsumerEmail}
          sendPreQualTermEmail={this.sendPreQualTermEmail}
          key={this.props.activeStep}
          clearFinancingPath={this.clearFinancingPath}
        />
      );
    }
  }

  const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  CalculateFinancing.displayName = `calculateFinancing(${wrappedComponentName})`;

  return CalculateFinancing;
}

export const stateToPropsConfig = {
  visitorId: bindToPath('id', VisitorModel),
  sessionId: bindToPath('session', VisitorModel),
  prequalificationData: bindToPath(PATHS.PREQUALIFICATION_DATA, FinancingModel),
};

export const connectedFinancingApplication = WrappedComponent =>
  withRouter(connectToModel(calculateFinancing(WrappedComponent), stateToPropsConfig));
